import { toHarnessPick, type Roster, type RosterPlayer } from './roster';
import { FIRST_BENCH_SLOT, type HarnessPick, type HarnessSquad } from './squad';

/**
 * Squad edits, kept as pure functions over a squad rather than as state inside
 * the panel. The scenarios these build are the reason the harness exists, so
 * they are worth testing directly rather than through a rendered picker.
 *
 * None of them enforce legality. `validateHarnessSquad` owns that, and it runs
 * over the result, so an edit is free to pass through an illegal intermediate
 * state on the way to a legal one. Refusing mid-edit would make some
 * legal squads unreachable: swapping a defender for a midfielder has to go
 * through a moment of having too many midfielders.
 */

const isStarterSlot = (slot: number): boolean => slot < FIRST_BENCH_SLOT;

const bySlot = (a: HarnessPick, b: HarnessPick): number => a.slot - b.slot;

const replacePick = (
  squad: HarnessSquad,
  slot: number,
  next: HarnessPick
): HarnessSquad => ({
  ...squad,
  picks: squad.picks.map((pick) => (pick.slot === slot ? next : pick))
});

/** Put a different player in a slot, keeping everything else as it was. */
export const swapPlayerIntoSlot = (
  squad: HarnessSquad,
  slot: number,
  player: RosterPlayer,
  rosters: Roster[]
): HarnessSquad =>
  replacePick(squad, slot, toHarnessPick(player, slot, rosters));

/**
 * Move a player between the XI and the bench by trading slots with somebody on
 * the other side.
 *
 * Trading rather than renumbering, because the squad is always fifteen slots
 * and moving one player without moving another would leave a hole. The
 * armbands follow their players rather than their slots, so promoting a
 * substitute cannot silently hand them the captaincy of whoever they replaced.
 */
export const tradeSlots = (
  squad: HarnessSquad,
  slot: number,
  otherSlot: number
): HarnessSquad => {
  const pick = squad.picks.find((one) => one.slot === slot);
  const other = squad.picks.find((one) => one.slot === otherSlot);
  if (!pick || !other) return squad;

  const followArmband = (armbandSlot: number): number => {
    if (armbandSlot === slot) return otherSlot;
    if (armbandSlot === otherSlot) return slot;
    return armbandSlot;
  };

  return {
    ...squad,
    picks: squad.picks.map((one) => {
      if (one.slot === slot) return { ...other, slot };
      if (one.slot === otherSlot) return { ...pick, slot: otherSlot };
      return one;
    }),
    captainSlot: followArmband(squad.captainSlot),
    viceCaptainSlot: followArmband(squad.viceCaptainSlot)
  };
};

/**
 * Give a player the armband, standing the other one down if it is the same
 * player, so the two can never end up on one player through a single click.
 */
export const setCaptain = (
  squad: HarnessSquad,
  slot: number
): HarnessSquad => ({
  ...squad,
  captainSlot: slot,
  viceCaptainSlot:
    squad.viceCaptainSlot === slot ? squad.captainSlot : squad.viceCaptainSlot
});

export const setViceCaptain = (
  squad: HarnessSquad,
  slot: number
): HarnessSquad => ({
  ...squad,
  viceCaptainSlot: slot,
  captainSlot:
    squad.captainSlot === slot ? squad.viceCaptainSlot : squad.captainSlot
});

export const setActiveChip = (
  squad: HarnessSquad,
  activeChip: HarnessSquad['activeChip']
): HarnessSquad => ({ ...squad, activeChip });

export const findPickBySlot = (
  squad: HarnessSquad,
  slot: number
): HarnessPick | undefined => squad.picks.find((pick) => pick.slot === slot);

export const startingPicks = (squad: HarnessSquad): HarnessPick[] =>
  squad.picks.filter((pick) => isStarterSlot(pick.slot)).sort(bySlot);

export const benchPicks = (squad: HarnessSquad): HarnessPick[] =>
  squad.picks.filter((pick) => !isStarterSlot(pick.slot)).sort(bySlot);
